import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "@/firebaseConfig.js";
import { useAuth } from "@/utils/auth.jsx";
import HeaderIcon from "@components/HeaderIcon.jsx";
import Button from "@components/Button.jsx";
import { FirebaseError } from "firebase/app";

const RESEND_COOLDOWN_SECONDS = 60;

const VerifyEmailPage = () => {
  const { user, loading, logout, refreshUser } = useAuth();
  const [checking, setChecking] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  // Poll in the background so the page redirects on its own once the user
  // clicks the link in their inbox, without needing a manual refresh.
  useEffect(() => {
    if (!user || user.emailVerified) return;

    pollRef.current = setInterval(() => {
      refreshUser();
    }, 4000);

    return () => clearInterval(pollRef.current);
  }, [user, refreshUser]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  if (loading) return null;
  if (!user) return <Navigate to="/auth/sign-in" replace />;
  if (user.emailVerified) return <Navigate to="/app" replace />;

  const handleCheckNow = async () => {
    setChecking(true);
    setError("");
    setInfo("");
    try {
      await refreshUser();
      if (!auth.currentUser?.emailVerified) {
        setInfo("Still not verified — check your inbox (and spam folder) and click the link, then try again.");
      }
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setInfo("");
    try {
      await sendEmailVerification(auth.currentUser);
      setInfo("Verification email sent. Please check your inbox and your SPAM (Unwanted) folder.");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      if (err instanceof FirebaseError && err.code === "auth/too-many-requests") {
        setError("Too many attempts — please wait a bit before requesting another email.");
      } else {
        setError("Couldn't send the email right now. Please try again shortly.");
      }
    }
  };

  return (
    <div className="authentication">
      <div className="wrapper">
        <HeaderIcon />
        <div className="authentication-box">
          <h2 className="authentication-box__title">Verify your email</h2>

          <p>
            We've sent a verification link to <strong>{user.email}</strong>. Click it to
            activate your account — this page will move on automatically once it's confirmed.
            Please check your inbox and don't forget to check your SPAM (Unwanted) folder too.
          </p>

          {info && <p className="authentication-box__success">{info}</p>}
          {error && <p className="authentication-box__error">{error}</p>}

          <Button
            title={checking ? "Checking..." : "I've verified — Check now"}
            onClick={handleCheckNow}
            disabled={checking}
          />

          <Button
            title={resendCooldown > 0 ? `Resend email (${resendCooldown}s)` : "Resend verification email"}
            onClick={handleResend}
            disabled={resendCooldown > 0}
          />

          <p className="authentication-box__text">
            Wrong account?{" "}
            <a className="authentication-box__link" href="#" onClick={(e) => { e.preventDefault(); logout(); }}>
              Log out
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
